import { z } from '@exodus/zod';
import { memoize } from '@exodus/basic-utils';
const createApplicationReport = ({ walletCreatedAtAtom, application, lockHistoryAtom, }) => ({
    namespace: 'application',
    export: async ({ isLocked, walletExists } = Object.create(null)) => {
        const [isRestoring, isBackedUp, lockHistory] = await Promise.all([
            application.isRestoring(),
            application.isBackedUp(),
            lockHistoryAtom.get(),
        ]);
        const serializedLockHistory = lockHistory.map((lockHistoryEntry) => ({
            ...lockHistoryEntry,
            timestamp: lockHistoryEntry.timestamp.toISOString(),
        }));
        if (isLocked) {
            return {
                isLocked,
                isRestoring,
                isBackedUp,
                lockHistory: serializedLockHistory,
            };
        }
        const getCreatedAt = async () => {
            if (!walletExists) {
                return null;
            }
            // We have a theory that this might take a while to fetch, so we add a timeout.
            // Consider removing this if we don't see any placeholder errors below in Safe Reports.
            let timerId;
            const timeout = new Promise((resolve) => {
                timerId = setTimeout(() => resolve('Cannot fetch wallet created at.'), 5000);
            });
            try {
                return await Promise.race([walletCreatedAtAtom.get(), timeout]);
            }
            finally {
                clearTimeout(timerId);
            }
        };
        return {
            isLocked,
            createdAt: await getCreatedAt(),
            isRestoring,
            isBackedUp,
            lockHistory: serializedLockHistory,
        };
    },
    getSchema: memoize(() => z
        .object({
        isLocked: z.boolean(),
        createdAt: z.string().nullable(),
        isRestoring: z.boolean(),
        isBackedUp: z.boolean(),
        lockHistory: z.array(z
            .object({
            locked: z.boolean(),
            timestamp: z.string(),
        })
            .strict()),
    })
        .strict()
        .partial()),
});
const applicationReportDefinition = {
    id: 'applicationReport',
    type: 'report',
    factory: createApplicationReport,
    dependencies: ['walletCreatedAtAtom', 'application', 'lockHistoryAtom'],
};
export default applicationReportDefinition;
